import { BarDatum } from '@nivo/bar';

import { FetchError } from '../../common/api/api';
import { Task } from '../tasks/Task.interface';
import { User } from '../../user/User.interface';
import { DueStatus } from '../dashboard/components/DueStatusLabel';
import { Group } from '../groups/Group.interface';

export interface FileConfig {
  // by default, show all available fields
  hideTaskKey?: boolean;
  hideVersion?: boolean;
  hideInternalDocumentId?: boolean;
  hideRev?: boolean;
  hideDescriptionOfChange?: boolean;
  hideReasonForChange?: boolean;
  hideChangeOrderReferenceCard?: boolean;
  showChangeOrderV2ReferenceCard?: boolean;
}

export enum FileStatus {
  Draft = 'draft',
  InReview = 'in-review',
  Approved = 'approved',
  Obsolete = 'obsolete'
}

export enum FileExportType {
  DocumentApproval = 'document-approval'
}

export interface FileMetadata {
  groups?: Pick<Group, 'id' | 'name'>[];
  source?: Required<Pick<FileDto, 'id' | 'name' | 'path'>>;
}

export interface FileDto {
  id: number;
  taskId?: number;
  task?: Task;
  name?: string;
  path?: string;
  version?: number;
  storageKey?: string;
  extension?: string;
  mimeType?: string;
  size?: number;
  internalDocumentId?: string;
  rev?: string;
  descriptionOfChange?: string;
  reasonForChange?: string;
  dueStatus?: DueStatus;
  fileVersion?: FileVersionDto;
  isSelected?: boolean; // used by drive file list
  isFolder?: boolean;
  isLocked?: boolean;
  isDeleted?: boolean;
  children?: FileDto[];
  parent?: FileDto;
  ownedBy?: User | null;
  groups?: Pick<Group, 'id' | 'name'>[];
  created?: Date;
  createdBy?: User;
  modified?: Date;
  modifiedBy?: User;
  uploaded?: Date;
  uploadedBy?: User;
  metadata?: FileMetadata;
}

export interface FileProgressDto extends FileDto {
  errorMessage?: string;
  isCompleted: boolean;
}

export interface CreateFolderDto {
  name: string;
  parentId?: number;
  isLocked?: boolean;
}

// NOTE: platform-stack data used for linking entities
export interface ReferenceData {
  id: number | string;
  tableName: string;
  relations: string[];
}

export interface ReferenceMetadata {
  type: string;
  data: Record<string, any>;
}

export interface CreateFileDto extends CreateFileVersionDto {
  name: string;
  taskTypeName: string;
  referenceData?: ReferenceData;
  referenceTaskId?: number;
  referenceMetadata?: ReferenceMetadata;
  parentId?: number | null;
  isLocked?: boolean;
  shouldUpdateIfExists?: boolean;
}

export interface CreateFileVersionDto {
  storageKey: string;
  mimeType: string;
  size: number;
  extension?: string;
  internalDocumentId?: string;
  descriptionOfChange?: string;
  reasonForChange?: string;
  referenceFileIds?: number[];
  changeOrderId?: number;
}

export interface FileVersionDto extends CreateFileVersionDto {
  id: number;
  version: number;
  status: FileStatus;
  uploaded?: Date;
  uploadedBy?: User;
  createdBy?: User;
  created: Date;
  task?: Task;
}

export type UpdateFileVersionDto =
  | CreateFileVersionDto
  | { status: FileStatus };

export interface MoveFileDto {
  id: number;
  destinationFolderId: number;
  shouldUpdateIfExists?: boolean;
}

export interface PresignFileDto {
  name: string;
  folderId: number;
  shouldUpdateIfExists?: boolean;
}

export type PresignedUrl = {
  storageKey: string;
  signedUrl: string;
  expiration: number;
};

export type UploadStatus =
  | 'init'
  | 'presign'
  | 'ready'
  | 'start'
  | 'uploading'
  | 'complete'
  | 'error'
  | 'cancel';
export interface DocumentUploadState {
  file?: File;
  presignedUrl?: PresignedUrl;
  status?: UploadStatus;
  progress: number;
  response?: Response;
  error?: any;
}

export interface FileLogDto {
  id: number;
  fileId: number;
  fileVersion?: FileVersionDto;
  name: string;
  version?: number;
  storageKey: string;
  extension: string;
  mimeType: string;
  size: number;
  isDeleted: boolean;
  log: string;
  metadata: any;
  ownedBy: User | null;
  createdBy: User;
  created: Date;
}

export enum DocumentType {
  File = 'file',
  Folder = 'folder'
}

export interface DocumentHistory {
  version: number;
  status: string;
  created: Date;
  createdBy: string;
}

export interface Document {
  id: number;
  taskId?: number;
  task?: Task;
  type: DocumentType;
  name: string;
  url?: string;
  version?: number;
  dueDate?: Date;
  modified: Date;
  assigned?: Date;
  modifiedBy: User;
  assignedTo?: User;
  documents?: Document[];
  history?: DocumentHistory[];
  fileVersion?: FileVersionDto;
}

export interface DocumentStat extends BarDatum {
  status: string;
  value: number;
}

export interface BaseDocumentCardProps {
  document: FileDto;
  onChange?: (document: Partial<FileDto>) => void;
  onUpdate?: () => void;
  onError?: (error: FetchError) => void;
}

export interface ApprovedFileVersion {
  id: number;
  name: string;
  version: number;
  storageKey: string;
  extension: string;
  mimetype: string;
  size: number;
  path: string;
  taskId: number;
  status: string;
  key: string;
}
